import { Close, Search } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import Tippy from "@tippyjs/react";
import { useCallback, useMemo, useState } from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { Users } from "../API/Users";
import { deepClone } from "../API/utils/deepClone";

type Props = { values: string[]; onChangeValues: Function };

export default function ClientsFilter({ values, onChangeValues }: Props) {
  const userAPI = useMemo(() => new Users(), []);
  const [data, setData] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const getGroups = useCallback(() => {
    userAPI.getGroups().then((response) => {
      const arrGroups: string[] = response.data.groups;
      arrGroups.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setData(arrGroups);
      setFilteredData(arrGroups);
    });
  }, [userAPI]);

  //   useEffect(() => {
  //     userAPI.getGroups().then((response) => {
  //       setData(response.data.groups);
  //       setFilteredData(response.data.groups);
  //     });
  //   }, [userAPI]);

  const onCheckHandler = useCallback(
    (checked: boolean, value: string) => {
      if (checked) {
        const _values = deepClone(values);
        _values.push(value);
        onChangeValues(_values);
      } else {
        const _values = values.filter((item) => item !== value);
        onChangeValues(_values);
      }
    },
    [onChangeValues, values]
  );
  const renderRow = useCallback(
    (props: ListChildComponentProps) => {
      const { index, style } = props;
      return (
        <ListItem
          style={style}
          key={index}
          component="div"
          sx={{ overflow: "hidden" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                onChange={(e) =>
                  onCheckHandler(e.target.checked, filteredData[index])
                }
                size="small"
                checked={values.indexOf(filteredData[index]) !== -1}
              />
            }
            label={
              <Typography textOverflow={"ellipsis"} overflow={"hidden"}>
                {filteredData[index]}
              </Typography>
            }
          />
        </ListItem>
      );
    },
    [filteredData, onCheckHandler, values]
  );

  const [searchText, setSearchText] = useState("");
  const onChangeSearch = useCallback(
    (text: any) => {
      const filtered = data.filter((item: string) => {
        let _item = item.toLocaleLowerCase();
        let _text = text.toLocaleLowerCase();
        return _item.includes(_text);
      });
      filtered.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setFilteredData(filtered);
      setSearchText(text);
    },
    [data]
  );
  const deselectAll = useCallback(() => {
    onChangeValues([]);
  }, [onChangeValues]);

  return (
    <Tippy
      interactive={true}
      allowHTML
      theme="security-tooltip"
      placement="bottom"
      trigger="click"
      onHide={() => {
        setFilteredData(data);
        setSearchText("");
        setIsOpen(false);
      }}
      onShow={() => {
        setIsOpen(true);
        if (!data.length) getGroups();
      }}
      content={
        <div>
          <Box
            maxWidth={500}
            bgcolor={"white"}
            p={1}
            border={1}
            borderRadius={1}
          >
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <TextField
                fullWidth
                placeholder="Search for a client"
                size="small"
                value={searchText}
                onChange={(e) => onChangeSearch(e.target.value)}
                InputProps={{
                  autoComplete: "off",
                  endAdornment:
                    searchText.length < 1 ? (
                      <IconButton>
                        <Search />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => onChangeSearch("")}>
                        <Close />
                      </IconButton>
                    ),
                }}
              />
              <Button
                disabled={values.length < 1}
                size="small"
                color="secondary"
                onClick={deselectAll}
              >
                Clear
              </Button>
            </Box>
            <FixedSizeList
              height={300}
              width={"auto"}
              itemSize={46}
              itemCount={filteredData.length}
              overscanCount={5}
            >
              {renderRow}
            </FixedSizeList>
          </Box>
        </div>
      }
    >
      <Box
        border={1}
        borderColor={"secondary"}
        p={1}
        display={"flex"}
        gap={1}
        borderRadius={1}
        maxWidth={300}
        sx={{ borderColor: "rgba(0, 0, 0, 0.23)" }}
      >
        <Box overflow={"hidden"}>
          <Typography
            textOverflow={"ellipsis"}
            overflow={"hidden"}
            whiteSpace={"nowrap"}
            sx={{
              color:
                values.length > 0
                  ? "rgba(0, 0, 0, 0.87)"
                  : "rgba(0, 0, 0, 0.6)",
            }}
          >
            {values.length > 0 ? values.join(", ") : "Clients"}
          </Typography>
        </Box>
        {isOpen ? (
          <ArrowDropUpIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />
        ) : (
          <ArrowDropDownIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />
        )}
      </Box>
    </Tippy>
  );
}
